import { Gift } from "../../../../api/giftCategories";
import { NavLink } from "react-router-dom";
import useOrderQuery from "../hooks/useOrderInfoQuery";
import useGiftOrderQuery from "../hooks/useGiftOrderQuery";
import useOrderGiftMutation from "../hooks/useOrderGiftMutation";
import React, { useCallback, useRef, useState, useEffect } from "react";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DSBox from "../../../system-design/DSBox";
import DSIconButton from "../../../system-design/DSIconButton";
import DSTextField from "../../../system-design/DSTextField";
import DSSwitch from "../../../system-design/DSSwitch";
import DSTypography from "../../../system-design/DSTypography";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import DSButton from "../../../system-design/DSButton";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogActions from "../../../system-design/DSDialogActions";
import useFileUpload from "../hooks/useFileUpload";
import ClockManagement from "./ClockManagement";
import KlydosPickDialog from "../../GiftCategories/components/KlydosPickDialog";

const GiftOrderModal = (props: {
  row: Gift;
  data: Gift[];
  setShowModal: (b: boolean) => void;
}) => {
  const [editGift, setEditGift] = useState<boolean>(false);
  const [showKlydoPicker, setShowKlydoPicker] = useState<boolean>(false);
  const orderName = props.row.orderName;

  // Fetch fresh gift order data from API
  const { data: giftOrder, refetch } = useGiftOrderQuery(orderName);
  const [localGiftOrder, setLocalGiftOrder] = useState<Gift>(props.row);

  const { data: orderInfo } = useOrderQuery(orderName);
  const {
    mutate: editOrderGift,
    mutateAsync: editOrderGiftAsync,
    isPending: isUpdatingClocks,
  } = useOrderGiftMutation(orderName);

  // Use fetched data or fallback to prop data
  const currentGiftOrder = giftOrder || props.row;

  // Initialize local state when fetched data changes or in edit mode
  useEffect(() => {
    if (editGift && currentGiftOrder) {
      setLocalGiftOrder(currentGiftOrder);
    }
  }, [editGift, currentGiftOrder]);

  // Use local state during editing, otherwise use fetched data
  const displayGiftOrder = editGift ? localGiftOrder : currentGiftOrder;
  const handleEditGift = () => {
    setEditGift(!editGift);
  };

  const handleGreetingContentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalGiftOrder({ ...localGiftOrder, greetingContent: e.target.value });
    },
    [localGiftOrder],
  );

  const handleSenderNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalGiftOrder({ ...localGiftOrder, senderName: e.target.value });
    },
    [localGiftOrder],
  );

  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const dateString = e.target.value;
      // Convert date string to timestamp
      const timestamp = dateString ? new Date(dateString).getTime() : undefined;
      setLocalGiftOrder({ ...localGiftOrder, date: timestamp });
    },
    [localGiftOrder],
  );

  const handleRemoveDate = useCallback(() => {
    setLocalGiftOrder({ ...localGiftOrder, date: null });
  }, [localGiftOrder]);

  const handleOrderNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalGiftOrder({
        ...localGiftOrder,
        orderName: e.target.value,
        clocksIds: [],
      });
    },
    [localGiftOrder],
  );

  const handleKlydoIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalGiftOrder({ ...localGiftOrder, klydoId: e.target.value });
    },
    [localGiftOrder],
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const defaultPhoto =
    "https://res.cloudinary.com/klydoclock/image/upload/profile/defaultPhoto_mc2iv6.png";

  const removeSenderImage = () => {
    setLocalGiftOrder({ ...localGiftOrder, senderImageUrl: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const { handleFileSelection, isPending } = useFileUpload({
    onError: () => {
      alert("Failed to upload image. Please try again.");
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onSuccess: (url) => {
      setLocalGiftOrder({ ...localGiftOrder, senderImageUrl: url });
    },
  });

  const handleProfileChangeClick = () => {
    if (editGift) {
      fileInputRef.current?.click();
    }
  };

  const handleChangeProfile = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelection(event);
  };

  const handleSaveGift = useCallback(() => {
    editOrderGift({ ...localGiftOrder });
    setEditGift(false);
  }, [localGiftOrder, editOrderGift]);

  const handleCreateNewOrder = useCallback(() => {
    window.open(
      `https://studio.klydoclock.com/gift/?basedOn=${currentGiftOrder.orderName}`,
      "_blank",
    );
  }, [currentGiftOrder.orderName]);
  return (
    <>
      <DSDialogTitle
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <DSBox sx={{ minWidth: 0 }}>
          <DSBox
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <DSTypography variant="h6" noWrap>
              {"Order Gift Details"}
            </DSTypography>
            <DSIconButton
              size="small"
              aria-label="edit gift"
              onClick={handleEditGift}
            >
              <EditIcon fontSize="small" />
            </DSIconButton>
          </DSBox>
          <DSTypography
            variant="body2"
            noWrap
            sx={{ color: "text.disabled", mt: 0.25 }}
          >
            {currentGiftOrder.orderName}
          </DSTypography>
        </DSBox>
        <DSIconButton
          aria-label="close"
          onClick={() => {
            props.setShowModal(false);
          }}
        >
          <CloseIcon />
        </DSIconButton>
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <DSTypography>
            <strong>Order Name:</strong>
            {editGift ? (
              <DSBox>
                <DSTextField
                  value={displayGiftOrder.orderName}
                  onChange={handleOrderNameChange}
                />
                <DSTypography color="error">
                  Once you edit the order Name and Save, the clock's gift will
                  be removed.
                </DSTypography>
              </DSBox>
            ) : (
              displayGiftOrder.orderName
            )}
          </DSTypography>
          <DSTypography>
            <strong>Gift Id:</strong> {currentGiftOrder.giftId}
          </DSTypography>
          <DSTypography>
            <strong>
              Manual override (mark if manually adjusted fields, to avoid
              alerts):
            </strong>{" "}
            <DSSwitch
              checked={!!currentGiftOrder?.manualOverride}
              onChange={(event) => {
                editOrderGift({ manualOverride: event.target.checked });
              }}
            />
          </DSTypography>
          <DSTypography>
            <strong>Sender Name:</strong>
            {editGift ? (
              <DSTextField
                value={displayGiftOrder.senderName}
                onChange={handleSenderNameChange}
              />
            ) : (
              displayGiftOrder.senderName
            )}
          </DSTypography>
          <DSTypography>
            <strong>Full Name: </strong>
            {`${orderInfo?.first_name} ${orderInfo?.last_name}`}
          </DSTypography>
          <DSTypography>
            <strong>Email: </strong>
            {orderInfo?.email}
          </DSTypography>
          <DSTypography>
            <strong>Greeting:</strong>
            {editGift ? (
              <DSTextField
                value={displayGiftOrder.greetingContent}
                onChange={handleGreetingContentChange}
                multiline
                fullWidth
                rows={5}
                maxRows={8}
                slotProps={{
                  input: {
                    inputProps: { maxLength: 160 },
                    style: {
                      padding: "20px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    },
                  },
                }}
                sx={{
                  ".MuiInputBase-input": {
                    overflowWrap: "break-word",
                    fontSize: "15px",
                    maxWidth: "300px",
                    display: "block",
                  },
                }}
              />
            ) : (
              displayGiftOrder.greetingContent
            )}
          </DSTypography>
          <DSTypography>
            <strong>Landing Page: </strong>
            {orderInfo?.firstVisit?.landingPage}
          </DSTypography>
          <DSTypography>
            <strong>Klydo ID:</strong>
            {editGift ? (
              <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DSTextField
                  value={displayGiftOrder.klydoId || ""}
                  onChange={handleKlydoIdChange}
                />
                <DSButton
                  variant="outlined"
                  size="small"
                  onClick={() => setShowKlydoPicker(true)}
                >
                  Pick
                </DSButton>
              </DSBox>
            ) : (
              <NavLink
                to="/admin/klydos"
                onClick={() => {
                  localStorage.setItem(
                    "klydossearch",
                    currentGiftOrder.klydoId,
                  );
                }}
              >
                {currentGiftOrder.klydoId || "-No klydo available"}
              </NavLink>
            )}
          </DSTypography>
          {editGift && showKlydoPicker && (
            <KlydosPickDialog
              selectedKlydos={
                displayGiftOrder.klydoId ? [displayGiftOrder.klydoId] : []
              }
              limit={1}
              greet={true}
              onPickKlydos={(ids) => {
                setLocalGiftOrder({
                  ...localGiftOrder,
                  klydoId: ids[0] ?? "",
                });
              }}
              onClose={() => setShowKlydoPicker(false)}
            />
          )}
          <DSTypography>
            <strong>Date:</strong>
            {editGift ? (
              <DSBox
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <DSTextField
                  type="date"
                  size="small"
                  value={
                    displayGiftOrder.date
                      ? new Date(displayGiftOrder.date)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleDateChange}
                />
                {displayGiftOrder.date && (
                  <DSButton
                    color="error"
                    size="small"
                    onClick={handleRemoveDate}
                  >
                    Remove Date
                  </DSButton>
                )}
              </DSBox>
            ) : displayGiftOrder.date ? (
              new Date(displayGiftOrder.date).toLocaleDateString()
            ) : (
              "-"
            )}
          </DSTypography>
          <ClockManagement
            clockIds={currentGiftOrder.clocksIds || []}
            includeNonRegistered
            isUpdating={isUpdatingClocks}
            emptyText="No clocks attached to this gift"
            onAddClock={(clockId) =>
              editOrderGiftAsync({
                clocksIds: [...(currentGiftOrder.clocksIds || []), clockId],
              }).then(() => refetch())
            }
            onRemoveClock={(clockId) =>
              editOrderGiftAsync({
                clocksIds: (currentGiftOrder.clocksIds || []).filter(
                  (id) => id !== clockId,
                ),
              }).then(() => refetch())
            }
            onClearAllClocks={() =>
              editOrderGiftAsync({ clocksIds: [] }).then(() => refetch())
            }
          />

          {editGift && (
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="image/*"
              onChange={handleChangeProfile}
            />
          )}

          {displayGiftOrder.senderImageUrl && (
            <DSBox>
              <DSTypography>
                <strong>Sender Image:</strong>
              </DSTypography>
              <img
                src={displayGiftOrder.senderImageUrl}
                alt="Sender"
                style={{
                  maxWidth: "100px",
                  borderRadius: "50%",
                  cursor: editGift ? "pointer" : "default",
                  border: editGift ? "2px dashed gray" : "none",
                }}
                onClick={handleProfileChangeClick}
              />
              {editGift && displayGiftOrder.senderImageUrl !== defaultPhoto && (
                <DSButton
                  color="error"
                  sx={{ marginLeft: "10px" }}
                  onClick={() => removeSenderImage()}
                >
                  Remove Image
                </DSButton>
              )}
            </DSBox>
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "flex-start" }}>
        <DSPrimaryButton
          onClick={() =>
            window.open(
              `https://studio.klydoclock.com/gift?orderName=${currentGiftOrder.orderName}`,
              "_blank",
            )
          }
        >
          Watch Gift
        </DSPrimaryButton>
        <DSPrimaryButton onClick={handleCreateNewOrder}>
          Create New Order Based on This Gift
        </DSPrimaryButton>
        {editGift && (
          <DSPrimaryButton onClick={handleSaveGift}>
            {isPending ? <DSCircularProgress size={15} /> : "Save"}
          </DSPrimaryButton>
        )}
      </DSDialogActions>
    </>
  );
};

export default GiftOrderModal;
